import fetch from './fetch';

/**
 * Performs a post request to the supplied url with the supplied data.
 *
 * @param {object} body
 * @param {string} url
 * @param {string} csrfmiddlewaretoken
 * @param {function} isValidResponseStatusCode
 * @param {function} started
 * @param {function} succeeded
 * @param {function} failed
 */
export const postRequest = (body, url, csrfmiddlewaretoken, isValidResponseStatusCode, started, succeeded, failed) => {
    const request = fetch(url, {
        body,
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'X-CSRFToken': csrfmiddlewaretoken,
        },
    });

    started(request);

    const response = request.then(response => {
        if (isValidResponseStatusCode(response.status)) {
            return response.json();
        } else {
            throw new Error(`Invalid response status: ${response.status}`);
        }
    });

    request.catch(error => failed(error, request));
    response.catch(error => failed(error, request));
    response.then(data => succeeded(data, request));

    return request;
};

/**
 * Handles the request for saving an object.
 *
 * @param {object} object
 * @param {object} data
 * @param {string} url
 * @param {string} csrfmiddlewaretoken
 * @param {function} started
 * @param {function} succeeded
 * @param {function} failed
 * @param {boolean} [create]
 */
const saveObject = (object, data, url, csrfmiddlewaretoken, started, succeeded, failed, create = true) => {
    return postRequest(
        data,
        url,
        csrfmiddlewaretoken,
        statusCode => statusCode === (create ? 201 : 200),
        request => started(object, request),
        (data, request) => succeeded({...object, ...data}, request),
        (error, request) => failed(object, error, request),
    );
};

/**
 * Handles the request for creating an object.
 *
 * @param {object} object
 * @param {object} data
 * @param {string} url
 * @param {string} csrfmiddlewaretoken
 * @param {function} started
 * @param {function} succeeded
 * @param {function} failed
 */
export const createObject = (object, data, url, csrfmiddlewaretoken, started, succeeded, failed) => {
    return saveObject(object, data, url, csrfmiddlewaretoken, started, succeeded, failed, true);
};

/**
 * Handles the request for updating an object.
 *
 * @param {object} object
 * @param {object} data
 * @param {string} url
 * @param {string} csrfmiddlewaretoken
 * @param {function} started
 * @param {function} succeeded
 * @param {function} failed
 */
export const updateObject = (object, data, url, csrfmiddlewaretoken, started, succeeded, failed) => {
    return saveObject(object, data, url, csrfmiddlewaretoken, started, succeeded, failed, false);
};

/**
 * Handles the request for deleting an object.
 *
 * @param {{delete_url: string, ...}} object
 * @param {string} csrfmiddlewaretoken
 * @param {function} started
 * @param {function} succeeded
 * @param {function} failed
 */
export const deleteObject = (object, csrfmiddlewaretoken, started, succeeded, failed) => {
    const request = fetch(object.delete_url, {
        method: 'DELETE',
        credentials: 'same-origin',
        headers: {
            'X-CSRFToken': csrfmiddlewaretoken,
        },
    });

    started(object);

    request.then(response => {
        if (response.status === 204 || response.status === 404) {
            succeeded(object);
        } else {
            failed(object);
        }
    });

    request.catch(() => failed(object));

    return request;
};



// WEBPACK FOOTER //
// ./scripts/utils/request.js